import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { activeHostMode } from "../utils/hostMode";
import { useAPI } from "@humansignal/core";

const DEFAULT_BRANDING = {
  name: "afriannotate",
  tagline: "Collaborative data annotation platform",
  owning_org: "masakhane-hub",
  support_email: "",
  primary_color: "",
  logo_url: "",
  // Narrow-display logomark. Menubar falls back to a letter avatar
  // when empty. Operators can supply a square glyph here and a wide
  // wordmark in ``logo_url``.
  icon_url: "",
  docs_url: "",
  api_docs_url: "",
  github_url: "",
  slack_url: "",
  discord_url: "",
  website_url: "",
  // Public privacy notice (sign-up consent line, footer, settings).
  privacy_url: "",
  // Typography (platform-wide source of truth). Empty strings mean
  // "use the platform CSS defaults" — ``applyTypography`` below only
  // sets the CSS variables that actually have a value, so anything
  // blank falls through to ``typography.prefix.css``.
  font_family: "",
  font_family_heading: "",
  font_family_mono: "",
  base_font_size: "",
  font_scale: 1.2,
  line_height: 1.5,
  heading_weight: 600,
  // Per-category size overrides. Each stores a ramp-tier name
  // ("100", "200", "250", …, "1000") or empty for "use the default
  // mapping from typography.prefix.css".
  font_size_page_title: "",
  font_size_section_title: "",
  font_size_body: "",
  font_size_caption: "",
  font_size_badge: "",
};

// Push typography values onto ``document.documentElement`` as CSS
// custom properties. Every token-aware style reads these via ``var()``
// so one server change cascades the platform. Variables set here:
//   --font-family, --font-family-heading, --font-family-mono
//   --font-size-400 (base), --font-scale, --line-height
//   --font-weight-heading
// Empty / missing values are cleared with ``removeProperty`` so we
// revert to the stylesheet defaults instead of pinning an empty string.
const applyTypography = (b) => {
  try {
    const root = document.documentElement;
    const set = (name, val) => {
      if (val === null || val === undefined || val === "") root.style.removeProperty(name);
      else root.style.setProperty(name, String(val));
    };
    set("--font-family", b.font_family);
    set("--font-family-heading", b.font_family_heading || b.font_family);
    set("--font-family-mono", b.font_family_mono);
    // ``--font-size-base`` is the single input that feeds the whole type
    // ramp. Semantic aliases (--font-size-{caption,body,title-lg,…}) and
    // numeric tiers (--font-size-100..1000) are all ``calc()``-derived
    // from it in typography.prefix.css, so setting this one var
    // rescales every size token at once.
    set("--font-size-base", b.base_font_size);
    // ``--font-scale`` is the second multiplier layered on top (e.g.
    // an accessibility +10% bump) — applied inside the same calc chain.
    set("--font-scale", b.font_scale != null ? String(b.font_scale) : "");
    set("--line-height", b.line_height != null ? String(b.line_height) : "");
    set("--font-weight-heading", b.heading_weight ? String(b.heading_weight) : "");
    // Per-category size overrides. The operator picks a ramp tier
    // (stored as a bare number like "250" or "700"); we translate to
    // ``var(--font-size-label)`` etc. and write it onto the matching
    // semantic alias. Empty / missing → removeProperty so the default
    // mapping from typography.prefix.css kicks back in.
    const sizeTierVar = (tier) => {
      if (!tier) return "";
      // Accept either "250" or a raw CSS length like "14px" — both are
      // valid values for a CSS var. Numbers-only strings are the
      // expected form from the Sizing picker.
      return /^\d+(\.\d+)?$/.test(String(tier).trim()) ? `var(--font-size-${String(tier).trim()})` : String(tier);
    };
    set("--font-size-page-title", sizeTierVar(b.font_size_page_title));
    set("--font-size-section-title", sizeTierVar(b.font_size_section_title));
    set("--font-size-body", sizeTierVar(b.font_size_body));
    set("--font-size-caption", sizeTierVar(b.font_size_caption));
    set("--font-size-badge", sizeTierVar(b.font_size_badge));
  } catch (_) {
    // Safe to ignore — style.setProperty can throw on very old browsers.
  }
};

const BrandingContext = createContext({ ...DEFAULT_BRANDING, refresh: async () => {} });

export const BrandingProvider = ({ children }) => {
  const api = useAPI();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);

  const load = useCallback(async () => {
    try {
      const res = await api.callApi("platformBranding");
      if (!res || res.error) return;
      const merged = { ...DEFAULT_BRANDING, ...res };
      setBranding(merged);
      applyTypography(merged);
      try {
        // A host mode that names its own product keeps its tab title.
        if (res.name && !activeHostMode?.baseTitle) document.title = res.name;
      } catch (_) {}
    } catch (_) {
      // Network failure / CSP / Safari-specific fetch errors — keep defaults.
    }
    // api identity is reconstructed when ApiContext re-memoizes on errors;
    // we only want to fetch branding once on mount (and via refresh()).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return <BrandingContext.Provider value={{ ...branding, refresh: load }}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext);
